/**
 * Views/Prenomina/IncidenciasTab.jsx
 *
 * Cuadrícula de incidencias diarias de un empleado.
 *
 * Diseño:
 *   - Columnas: cada día del periodo (1, 2, ... N) + columna Totales
 *   - Filas: tipos de incidencia dinámicos (vienen de tiposIncidencias del catálogo)
 *   - Celda: input numérico con la cantidad de esa incidencia en ese día
 *
 * Al editar una celda y salir del foco, se llama a onGuardarDia(diaPeriodo, fecha, entradas[]).
 */

import React, { Component } from 'react'
import { Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'

const DIAS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

// Paleta de colores para tipos dinámicos
const COLOR_PALETTE = [
  '#ff4d4f', '#faad14', '#52c41a', '#722ed1',
  '#13c2c2', '#eb2f96', '#1677ff', '#8c8c8c',
  '#fa8c16', '#2f54eb', '#389e0d', '#c41d7f',
]

function mapearTiposInc(tipos = []) {
  return tipos.map((t, i) => ({
    id:    t._id,
    tipo:  t.clave,
    label: t.nombre,
    color: COLOR_PALETTE[i % COLOR_PALETTE.length],
  }))
}

const TH = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '4px 6px', fontSize: 11, fontWeight: 600,
  color: '#555', textAlign: 'center', whiteSpace: 'nowrap',
}
const TD = {
  border: '1px solid #e8e8e8', padding: '2px 4px',
  fontSize: 11, textAlign: 'center', verticalAlign: 'middle',
}

// ─────────────────────────────────────────────────────────────────────────────
class IncidenciasTab extends Component {

  constructor(props) {
    super(props)
    this.state = {
      // celdas[tipo][diaPeriodo] = cantidad
      celdas: this.initCeldas(props.linea, mapearTiposInc(props.tiposIncidencias)),
      // permanentes[`${tipo}|${diaPeriodo}`] = true — celdas que generó una
      // incidencia permanente (se muestran marcadas para que se sepa de dónde salieron)
      permanentes: this.initPermanentes(props.linea, mapearTiposInc(props.tiposIncidencias)),
    }
  }

  componentDidUpdate(prevProps) {
    const tiposChanged = prevProps.tiposIncidencias !== this.props.tiposIncidencias
    const lineaChanged = prevProps.linea !== this.props.linea
    if (tiposChanged || lineaChanged) {
      const tiposInc = this.getTiposInc()
      this.setState({
        celdas: this.initCeldas(this.props.linea, tiposInc),
        permanentes: this.initPermanentes(this.props.linea, tiposInc),
      })
    }
  }

  /** Marca qué celdas vienen de una incidencia permanente */
  initPermanentes(linea, tiposInc = []) {
    const idATipo = {}
    for (const t of tiposInc) if (t.id) idATipo[t.id] = t.tipo

    const permanentes = {}
    for (const dia of (linea?.incidencias || [])) {
      for (const entrada of dia.entradas) {
        if (!entrada.incidenciaPermanenteId) continue
        const tipoKey = idATipo[entrada.tipoIncidenciaId] ?? entrada.tipo
        if (tipoKey !== undefined) permanentes[`${tipoKey}|${dia.diaPeriodo}`] = true
      }
    }
    return permanentes
  }

  /**
   * Entrada tal como está guardada en la línea, para ese día y ese tipo.
   * Se usa para no perder metadatos (p.ej. incidenciaPermanenteId) al
   * reconstruir el día en handleCelda.
   */
  getEntradaOriginal(diaPeriodo, t) {
    const dia = (this.props.linea?.incidencias || []).find(d => d.diaPeriodo === diaPeriodo)
    if (!dia) return null
    return (dia.entradas || []).find(e =>
      (e.tipoIncidenciaId && t.id && e.tipoIncidenciaId === t.id) || e.tipo === t.tipo
    ) || null
  }

  getTiposInc() {
    return mapearTiposInc(this.props.tiposIncidencias || [])
  }

  initCeldas(linea, tiposInc = []) {
    const celdas = {}
    const idATipo = {}
    for (const t of tiposInc) {
      celdas[t.tipo] = {}
      if (t.id) idATipo[t.id] = t.tipo
    }

    // Ojo: celdas[] se indexa por la CLAVE del TipoIncidencia, así que una entrada
    // sin tipoIncidenciaId válido cae al fallback entrada.tipo y se descarta sin
    // avisar. El backend siempre debe guardar el id resuelto del catálogo.
    for (const dia of (linea?.incidencias || [])) {
      for (const entrada of dia.entradas) {
        const tipoKey = idATipo[entrada.tipoIncidenciaId] ?? entrada.tipo
        if (tipoKey !== undefined && celdas[tipoKey] !== undefined) {
          celdas[tipoKey][dia.diaPeriodo] = entrada.cantidad
        }
      }
    }
    return celdas
  }

  getDias() {
    const { fechaInicio, fechaFin } = this.props.prenomina || {}
    if (!fechaInicio || !fechaFin) return []
    const n = dayjs(fechaFin).diff(dayjs(fechaInicio), 'day') + 1
    if (n <= 0) return []
    return Array.from({ length: n }, (_, i) => i + 1)
  }

  getFechaDelDia(diaPeriodo) {
    const inicio = this.props.prenomina?.fechaInicio
    if (!inicio) return null
    return dayjs(inicio).add(diaPeriodo - 1, 'day')
  }

  async handleCelda(tipo, diaPeriodo, cantidad) {
    const { celdas } = this.state
    const tiposInc = this.getTiposInc()

    // El input ya va `disabled` en esos días, pero handleCelda también se dispara desde el
    // onBlur, así que el guard va aquí también. El candado de verdad está en el servidor
    // (guardarIncidenciaDia), no en la rejilla.
    if (this.fueraDeRelacion(diaPeriodo, this.getVentanaRelacion())) return

    const nuevas = {
      ...celdas,
      [tipo]: { ...celdas[tipo], [diaPeriodo]: cantidad },
    }
    this.setState({ celdas: nuevas })

    const entradas = []
    for (const t of tiposInc) {
      const cant = (nuevas[t.tipo] || {})[diaPeriodo]
      if (!(cant > 0)) continue

      // Si la cantidad no cambió, se reusa la entrada tal cual está guardada.
      // Así no se pierde la marca incidenciaPermanenteId de las entradas que
      // generó una incidencia permanente y el usuario no tocó (el motor las
      // sigue refrescando). Si el usuario sí cambió la cantidad, la entrada se
      // reconstruye sin marca y queda como captura manual: desde ese momento la
      // incidencia permanente ya no la pisa.
      const original = this.getEntradaOriginal(diaPeriodo, t)
      if (original && original.cantidad === cant) { entradas.push(original); continue }

      entradas.push({ tipo: t.tipo, tipoIncidenciaId: t.id, cantidad: cant, descripcion: '' })
    }

    const fecha = this.getFechaDelDia(diaPeriodo)
    await this.props.onGuardarDia(diaPeriodo, fecha?.toISOString(), entradas)
  }

  async limpiarDia(diaPeriodo) {
    const { celdas } = this.state
    const tiposInc = this.getTiposInc()
    if (this.fueraDeRelacion(diaPeriodo, this.getVentanaRelacion())) return
    const nuevas = { ...celdas }
    for (const t of tiposInc) {
      if (nuevas[t.tipo]) {
        const { [diaPeriodo]: _, ...resto } = nuevas[t.tipo]
        nuevas[t.tipo] = resto
      }
    }
    this.setState({ celdas: nuevas })
    await this.props.onGuardarDia(diaPeriodo, null, [])
  }

  getTotalTipo(tipo) {
    return Object.values(this.state.celdas[tipo] || {}).reduce((s, v) => s + (v || 0), 0)
  }

  getTotalDia(diaPeriodo) {
    return this.getTiposInc().reduce((s, t) => {
      return s + ((this.state.celdas[t.tipo] || {})[diaPeriodo] || 0)
    }, 0)
  }

  /**
   * Posiciones (1-based) del día de descanso dentro del periodo, del snapshot de la Prenómina:
   * semanal [7], catorcenal [7, 14], quincenal y mensual vacío.
   *
   * Se marcan porque el motor trata esos días distinto y no había forma de saberlo desde aquí:
   * una incidencia capturada en un día de descanso NO descuenta sueldo ni séptimo día (el
   * cálculo salta esas posiciones), pero SÍ cuenta para el ausentismo de IMSS. De ahí que se
   * marque en vez de bloquear la captura — una ausencia de semana completa necesita el día de
   * descanso capturado para que el ausentismo salga en 7 días y no en 6.
   */
  getSeptimos() {
    return new Set((this.props.prenomina?.posisionSeptimoDia || []).map(Number))
  }

  /**
   * Ventana laborable del empleado dentro del periodo, 1-based e inclusiva. La inyecta
   * datosPrenomina en cada línea (LineaPrenomina no guarda la fecha de baja), calculada con el
   * mismo helper que usa el motor — aquí NO se rehace la aritmética de fechas para que las dos
   * no puedan desalinearse.
   *
   * Los días de fuera se marcan y se bloquean: el motor los recorta del sueldo, así que lo que
   * se capture ahí no descuenta nada. Sin ausencia de datos la ventana cubre todo el periodo.
   */
  getVentanaRelacion() {
    const { linea } = this.props
    const dias = this.getDias().length
    return {
      posInicio: Number(linea?.posInicioRelacion) || 1,
      posFin: Number.isFinite(Number(linea?.posFinRelacion)) ? Number(linea.posFinRelacion) : dias,
    }
  }

  fueraDeRelacion(diaPeriodo, ventana) {
    return diaPeriodo < ventana.posInicio || diaPeriodo > ventana.posFin
  }

  render() {
    const { celdas } = this.state
    const tiposInc = this.getTiposInc()
    const dias     = this.getDias()
    const septimos = this.getSeptimos()
    const ventana  = this.getVentanaRelacion()
    const hayFuera = dias.some(d => this.fueraDeRelacion(d, ventana))

    if (tiposInc.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 32, color: '#aaa' }}>
          No hay tipos de incidencia configurados en el catálogo.
        </div>
      )
    }

    return (
      <div>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          Registra las incidencias por día del periodo. Ingresa la <b>cantidad</b> de cada
          tipo (p.ej. 2 retardos). Los días sin incidencias no necesitan llenarse.
        </p>

        <div style={{ overflowX: 'auto', maxHeight: 400 }}>
          <table style={{ borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <th style={{ ...TH, textAlign: 'left', minWidth: 140, position: 'sticky', left: 0, zIndex: 3, background: '#f5f5f5' }}>
                  Incidencia
                </th>
                {dias.map(d => {
                  const fecha  = this.getFechaDelDia(d)
                  const esFin  = fecha && (fecha.day() === 0 || fecha.day() === 6)
                  const esSep  = septimos.has(d)
                  const fuera  = this.fueraDeRelacion(d, ventana)
                  const totalD = this.getTotalDia(d)
                  return (
                    <th
                      key={d}
                      title={fuera
                        ? 'Fuera de la relación laboral: el empleado no estaba dado de alta este día. No se puede capturar y no cuenta para el cálculo.'
                        : esSep
                          ? 'Día de descanso (séptimo día). Lo que se capture aquí no descuenta sueldo ni séptimo día, pero sí cuenta para el ausentismo de IMSS.'
                          : undefined}
                      style={{
                        ...TH, minWidth: 46,
                        background: fuera ? '#f0f0f0' : esSep ? '#e6f4ff' : esFin ? '#fff7e6' : '#f5f5f5',
                        color: fuera ? '#bbb' : TH.color,
                        borderTop: esSep && !fuera ? '2px solid #1677ff' : TH.border,
                      }}
                    >
                      <div>{fecha ? fecha.format('DD') : d}</div>
                      {fecha && <div style={{ fontSize: 9, color: fuera ? '#bbb' : esSep ? '#1677ff' : esFin ? '#faad14' : '#aaa', fontWeight: 400 }}>{DIAS_ES[fecha.day()]}</div>}
                      {fuera && <div style={{ fontSize: 9, color: '#bbb', fontWeight: 700, lineHeight: 1.1 }}>—</div>}
                      {!fuera && esSep && <div style={{ fontSize: 9, color: '#1677ff', fontWeight: 700, lineHeight: 1.1 }}>7º</div>}
                      {!fuera && totalD > 0 && (
                        <Tooltip title="Limpiar este día">
                          <div
                            onClick={() => this.limpiarDia(d)}
                            style={{ cursor: 'pointer', color: '#faad14', fontSize: 9 }}
                          >
                            ✕
                          </div>
                        </Tooltip>
                      )}
                    </th>
                  )
                })}
                <th style={{ ...TH, minWidth: 60, background: '#e6f7ff' }}>Total</th>
              </tr>
            </thead>

            <tbody>
              {tiposInc.map(({ tipo, label, color }) => (
                <tr key={tipo}>
                  <td style={{
                    ...TD, textAlign: 'left', fontWeight: 500,
                    background: '#fafafa', position: 'sticky', left: 0,
                    borderRight: '2px solid #d0d0d0',
                  }}>
                    <Tag color={color} style={{ fontSize: 10, margin: 0 }}>{label}</Tag>
                  </td>

                  {dias.map(d => {
                    const fecha = this.getFechaDelDia(d)
                    const esFin = fecha && (fecha.day() === 0 || fecha.day() === 6)
                    const esSep = septimos.has(d)
                    const fuera = this.fueraDeRelacion(d, ventana)
                    const val   = (celdas[tipo] || {})[d] || ''
                    const esPerm = !!this.state.permanentes[`${tipo}|${d}`]

                    return (
                      <td
                        key={d}
                        title={fuera
                          ? 'Fuera de la relación laboral del empleado'
                          : esPerm
                            ? 'Generada por una incidencia permanente'
                            : esSep
                              ? 'Día de descanso (séptimo día): no descuenta sueldo ni séptimo día'
                              : undefined}
                        style={{
                          ...TD,
                          background: fuera ? '#f0f0f0' : esSep ? '#f5fbff' : esFin ? '#fff7e6' : 'white',
                          boxShadow: esPerm && !fuera ? 'inset 0 -2px 0 #722ed1' : undefined,
                        }}
                      >
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={val}
                          placeholder={fuera ? '' : '—'}
                          disabled={fuera}
                          style={{
                            width: 36, textAlign: 'center', border: 'none',
                            background: 'transparent', fontSize: 11,
                            color: val > 0 ? color : '#ccc',
                            fontWeight: val > 0 ? 700 : 400,
                            outline: 'none',
                            cursor: fuera ? 'not-allowed' : undefined,
                          }}
                          onChange={(e) => {
                            const nuevas = {
                              ...celdas,
                              [tipo]: { ...celdas[tipo], [d]: e.target.value === '' ? undefined : Number(e.target.value) },
                            }
                            this.setState({ celdas: nuevas })
                          }}
                          onBlur={(e) => {
                            const cant = parseFloat(e.target.value)
                            this.handleCelda(tipo, d, isNaN(cant) || cant <= 0 ? 0 : cant)
                          }}
                        />
                      </td>
                    )
                  })}

                  <td style={{ ...TD, background: '#e6f7ff', fontWeight: 700, color }}>
                    {this.getTotalTipo(tipo) || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: '#aaa' }}>
          Los fines de semana aparecen en fondo amarillo. Las incidencias se guardan automáticamente al salir de cada celda.
          Las celdas con línea morada abajo las generó una incidencia permanente (pestaña "Incid. Permanentes"); si cambias
          su cantidad, deja de refrescarse y queda como captura manual.
          {septimos.size > 0 && (
            <> Las columnas azules marcadas <b style={{ color: '#1677ff' }}>7º</b> son el <b>día de descanso</b>:
            lo que se capture ahí <b>no descuenta sueldo ni séptimo día</b>, pero sí cuenta para el ausentismo de IMSS
            (por eso hay que capturarlo cuando la ausencia es de la semana completa).</>
          )}
          {hayFuera && (
            <> Las columnas <b>grises</b> quedan <b>fuera de la relación laboral</b> del empleado
            {ventana.posFin < dias.length ? ' (posteriores a su fecha de baja)' : ''}
            {ventana.posInicio > 1 ? ' (anteriores a su fecha de alta)' : ''}: esos días no se pagan
            ni cotizan, así que no se pueden capturar y no cuentan para el cálculo.</>
          )}
        </div>
      </div>
    )
  }
}

export default IncidenciasTab
